import classNames from 'classnames';
import React, { useMemo, useState } from 'react';

import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import type { EFaktura } from '../../generated-types';
import useEFakturaAccount from '../../hooks/useEFakturaAccount';
import usePaymentAccount from '../../hooks/usePaymentAccount';
import isServiceError from '../../utils/isServiceError';
import { Content } from '../shared/Content';
import EFakturaModal from '../transaction/EFaktura/EFakturaModal';
import EFakturaApprovalListLink from './EFakturaApprovalListLink';
import EFakturaApprovalListSkeleton from './EFakturaApprovalListSkeleton';
import EFakturaEmptyList from './EFakturaEmptyList';
import { EFakturaItem } from './EFakturaItem';
import EFakturaListError from './EFakturaListError';

import './EFakturaList.scss';

const maxVisibleEfakturas = 3;

interface Props {
    eFakturas: EFaktura[];
    isLoading: boolean;
    error: RequestExceptionWithResponse | null;
    type: 'task-manager-list' | 'full-list';
}

const EFakturaList: React.FC<Props> = ({ eFakturas, isLoading, type, error }) => {
    const [showEFakturaModal, setShowEFakturaModal] = useState(false);
    const [viewedEfaktura, setViewedEfaktura] = useState<EFaktura>();

    const eFakturaAccount = useEFakturaAccount();
    const fromAccount = usePaymentAccount();

    const openModal = (eFaktura: EFaktura) => {
        setViewedEfaktura(eFaktura);
        setShowEFakturaModal(true);
    };

    const visibleItems = useMemo(
        () => (type === 'full-list' ? eFakturas : eFakturas.slice(0, maxVisibleEfakturas)),
        [eFakturas, type],
    );

    if (isLoading) {
        const maxSkeletonLoading = 2;
        const loadCount =
            eFakturas.length > maxSkeletonLoading || eFakturas.length === 0 ? maxSkeletonLoading : eFakturas.length;

        return <EFakturaApprovalListSkeleton darkSkeleton={type === 'task-manager-list'} count={loadCount} />;
    }

    if (error) {
        if (isServiceError(error)) return <></>;
        return (
            <Content className="frontpage-efaktura-error">
                <EFakturaListError error={error} />
            </Content>
        );
    }

    if (eFakturas.length === 0 && type === 'full-list') return <EFakturaEmptyList />;

    return (
        <>
            <div className="eFaktura-list">
                <ul
                    className={classNames('eFaktura-list__container', {
                        'show-more': eFakturas.length > maxVisibleEfakturas && type === 'task-manager-list',
                    })}
                    data-testid="efaktura-list"
                >
                    {visibleItems.map((eFaktura) => (
                        <EFakturaItem
                            showPaymentTypeLabel={
                                type === 'full-list' || (type === 'task-manager-list' && visibleItems.length === 1)
                            }
                            alternateLook={type === 'full-list' || visibleItems.length === 1}
                            eFaktura={eFaktura}
                            key={eFaktura.id.value}
                            openModal={openModal}
                        />
                    ))}
                </ul>

                {eFakturas.length > maxVisibleEfakturas && type === 'task-manager-list' && (
                    <EFakturaApprovalListLink fromAccountId={eFakturaAccount?.accountId} />
                )}
            </div>
            {viewedEfaktura && showEFakturaModal && (
                <EFakturaModal
                    account={eFakturaAccount || fromAccount || undefined}
                    show={showEFakturaModal}
                    eFaktura={viewedEfaktura}
                    setShow={setShowEFakturaModal}
                />
            )}
        </>
    );
};

export default EFakturaList;
